import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { getProductPriceIds, getPlanByKey } from '../config/subscriptionPlans';
import { callFunction } from '../services/api';
import { useUserData } from '../hooks';
import styles from './CompletePayment.module.css';

const CompletePayment = () => {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { userData, loading: userDataLoading } = useUserData();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        
        await currentUser.getIdToken(true);
        const tokenResult = await currentUser.getIdTokenResult();
        const role = tokenResult.claims.role;
        
        if (role === 'active_user') {
          navigate('/app/mls-gpt');
          return;
        }
      } else {
        navigate('/signin');
      }
      setAuthChecked(true);
    });

    return () => unsubscribe();
  }, [navigate]);

  const loading = !authChecked || userDataLoading;

  const handleRetryPayment = async () => {
    setRedirecting(true);
    setError('');

    try {
      if (!user) {
        throw new Error('Not signed in');
      }

      // Get user's selected plan from Firestore or default to first plan
      let priceId;
      if (userData?.subscriptionTier) {
        const plan = getPlanByKey(userData.subscriptionTier);
        if (plan) {
          priceId = getProductPriceIds()[plan.key];
        }
      }
      
      // Fallback to first available plan if no plan set
      if (!priceId) {
        const plans = Object.keys(getProductPriceIds());
        if (plans.length > 0) {
          priceId = getProductPriceIds()[plans[0]];
        }
      }

      if (!priceId) {
        throw new Error('No subscription plan available');
      }

      const data = await callFunction('createCheckoutSession', {
        priceId,
        successUrl: `${window.location.origin}/payment-success`,
        cancelUrl: `${window.location.origin}/complete-payment`
      });
      
      const { url } = data;
      
      // Redirect to Stripe Checkout
      window.location.href = url;
      
    } catch (error) {
      console.error('Retry payment error:', error);
      setError(error.message || 'Failed to start payment. Please try again.');
      setRedirecting(false);
    }
  };

  const handleContactSupport = () => {
    navigate('/contact');
  };

  if (loading) {
    return (
      <div className={styles.completePaymentContainer}>
        <div className={styles.completePaymentCard}>
          <h1>Loading...</h1>
        </div>
      </div>
    );
  }

  if (!user || !userData) {
    return null; // Will redirect
  }

  const status = userData.status || 'pending_payment';
  const isNewSignup = status === 'pending_payment';
  const isPaymentOverdue = status === 'payment_overdue';

  return (
    <div className={styles.completePaymentContainer}>
      <div className={styles.completePaymentCard}>
        <h1>Complete Your Payment Setup</h1>
        
        {isNewSignup && (
          <div className={`${styles.statusMessage} ${styles.info}`}>
            <h2>Payment Setup Required</h2>
            <p>Your account has been created, but payment setup is required to activate your subscription.</p>
          </div>
        )}

        {isPaymentOverdue && (
          <div className={`${styles.statusMessage} ${styles.error}`}>
            <h2>Payment Overdue</h2>
            <p>Your subscription payment could not be processed. Please update your payment method to restore access to your account.</p>
          </div>
        )}

        {error && (
          <div className={styles.errorMessage}>
            <p>{error}</p>
          </div>
        )}

        <div className={styles.actionButtons}>
          <button 
            onClick={handleRetryPayment}
            disabled={redirecting}
            className={styles.primaryButton}
          >
            {redirecting ? 'Redirecting to Stripe...' : isPaymentOverdue ? 'Update Payment Method' : 'Complete Payment'}
          </button>
          
          <button 
            onClick={() => navigate('/signin')}
            className={styles.secondaryButton}
          >
            Back to Sign In
          </button>
        </div>

        <div className={styles.helpText}>
          <p>
            Need help? Contact our support team at{' '}
            <a href="mailto:support@domusiq.com">support@domusiq.com</a>
            {' '}or visit our{' '}
            <a href="/contact" onClick={(e) => { e.preventDefault(); navigate('/contact'); }}>
              contact page
            </a>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CompletePayment;
